import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'react-feather';
import classNames from 'classnames';
import { Button } from 'react-bootstrap';

import styles from './MonthlyStepper.module.scss';

import { getYearsFromMonth, parseYearMonth } from './utils';
import useFadeHeaderAnimation from './useFadeHeaderAnimation';
import { IYearMonthlyAggregate, StepperAction } from './types';
import { STEPPER_CONFIG } from './config';

interface MonthlyStepperHeaderProps {
  selectedHeaderMonths: any[];
  yearRange: any[];
  handlePrevious: () => void;
  handleNext: () => void;
  actionType?: StepperAction;
  isAnimating?: boolean;
  enableAnimation?: boolean;
}

const MonthlyStepperTopHeader = ({
  selectedHeaderMonths,
  handlePrevious,
  handleNext,
  yearRange,
  actionType,
  isAnimating,
  enableAnimation = true,
}: MonthlyStepperHeaderProps) => {
  const [/* isNexLinkDisabled */, setNextLinkDisabled] = useState(false);
  const [/* isPrevLinkDisabled */, setPrevLinkDisabled] = useState(false);
  const [headerGroupList, setHeaderGroupList] = useState<IYearMonthlyAggregate[]>([]);
  const [yearOffset, yearLimit] = useMemo(
    () => [parseYearMonth(yearRange[0]), parseYearMonth(yearRange[1])],
    [yearRange.join(',')],
  );

  // Fade animation for year/month header rows
  const computedFadeClass = useFadeHeaderAnimation([selectedHeaderMonths.map((m) => `${m.year}-${m.monthIndex}`).join(',')]);
  const fadeClass = enableAnimation ? computedFadeClass : '';

  useEffect(() => {
    const firstMonth = selectedHeaderMonths[0];
    const lastMonth = selectedHeaderMonths[selectedHeaderMonths.length - 1];

    if (firstMonth && firstMonth.year === yearOffset.year && firstMonth.monthIndex === yearOffset.month) {
      setPrevLinkDisabled(true);
    } else {
      setPrevLinkDisabled(false);
    }

    if (
      lastMonth &&
      lastMonth.year === yearLimit.year &&
      (lastMonth.monthIndex === yearLimit.month ||
        (selectedHeaderMonths.length < STEPPER_CONFIG.displayColumns && lastMonth.monthIndex < yearLimit.month!))
    ) {
      setNextLinkDisabled(true);
    } else {
      setNextLinkDisabled(false);
    }

    setHeaderGroupList(() => {
      const years = getYearsFromMonth(selectedHeaderMonths);

      return years;
    });
  }, [selectedHeaderMonths]);

  return (
    <table>
      <tr>
        <td>
          <table className="stepper-table-header">
            <span className="_left-button">
              <Button className="app-btn-secondary h-auto" onClick={handlePrevious} disabled={isAnimating}>
                <ChevronLeft />
              </Button>
            </span>
            <tr className={classNames('years')}>
              {headerGroupList.map((item, index) => {
                return (
                  <td key={item.year} colSpan={item.numberOfMonths} className="fs-name theme-text">
                    <div className={fadeClass ? (styles[fadeClass] ?? '') : ''}><span>{item.year}</span></div>
                  </td>
                );
              })}
            </tr>
            <tr className={classNames('month-col')}>
              {selectedHeaderMonths.map((value) => {
                return (
                  <td className={classNames('fs-name theme-text')} key={value.monthName}>
                    <div className={fadeClass ? (styles[fadeClass] ?? '') : ''}><span>{value.monthName}</span></div>
                  </td>
                );
              })}
            </tr>
            <span className="_right-button">
              <Button
                onClick={handleNext}
                className="app-btn-secondary h-auto"
                disabled={isAnimating}
              >
                <ChevronRight />
              </Button>
            </span>
          </table>
        </td>
      </tr>
    </table>
  );
};

export default MonthlyStepperTopHeader;
